// AntScore — «диктор» (announcer). Порт эталона spec/contract/reference/announcer.mjs 1-в-1 на TS.
//
// Чистая функция поверх двух снимков состояния движка: что произнести при переходе
// prev -> next (после addPoint). Клиент озвучивает фразы через платформенный TTS (ru-RU).
//
// Приоритет события: матч > сет > гейм > очко (на одном переходе озвучивается ровно одно событие).

import type { Snapshot, Team } from './engine';

const SCORE_WORDS: Record<string, string> = {
  '0': 'ноль',
  '15': 'пятнадцать',
  '30': 'тридцать',
  '40': 'сорок',
  Ad: 'больше',
};

const CARDINAL = [
  'ноль', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять',
  'десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать',
  'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать', 'двадцать',
];

function num(n: number): string {
  return n >= 0 && n < CARDINAL.length ? CARDINAL[n] : String(n);
}

function teamName(snap: Snapshot, t: Team | null): string {
  return (
    (t && snap.teams && snap.teams[t] && snap.teams[t].name) ||
    (t === 'A' ? 'Синие' : 'Красные')
  );
}

/**
 * @param prev  состояние ДО очка (computeState до addPoint)
 * @param next  состояние ПОСЛЕ очка (computeState после addPoint)
 * @returns фразы для произнесения (ru). Пусто, если озвучивать нечего.
 */
export function announce(prev: Snapshot, next: Snapshot): string[] {
  // Матч завершён
  if (next.status === 'finished' && prev.status !== 'finished') {
    return ['Матч окончен.', 'Победа: ' + teamName(next, next.winner) + '.'];
  }
  // Завершён сет
  if (next.completedSets.length > prev.completedSets.length) {
    return ['Сет.', 'По сетам ' + num(next.setsWonA) + ' — ' + num(next.setsWonB) + '.'];
  }
  // Завершён гейм (изменилось число геймов, новый сет не начат)
  const prevGames = prev.gamesA + prev.gamesB;
  const nextGames = next.gamesA + next.gamesB;
  if (nextGames !== prevGames) {
    return ['Гейм.', 'По геймам ' + num(next.gamesA) + ' — ' + num(next.gamesB) + '.'];
  }
  // Обычное очко
  if (next.inTieBreak) {
    const [f, s] = next.server === 'A' ? [next.tbA, next.tbB] : [next.tbB, next.tbA];
    return [num(f) + ' — ' + num(s) + '.'];
  }
  if (next.isDeuce) return ['Ровно.'];
  if (next.displayA === 'Ad') return ['Больше. ' + teamName(next, 'A') + '.'];
  if (next.displayB === 'Ad') return ['Больше. ' + teamName(next, 'B') + '.'];
  // счёт гейма, первым — счёт подающей команды (как принято в падел/теннисе)
  const wa = SCORE_WORDS[next.displayA] ?? next.displayA;
  const wb = SCORE_WORDS[next.displayB] ?? next.displayB;
  const [f, s] = next.server === 'A' ? [wa, wb] : [wb, wa];
  return [f + ' — ' + s + '.'];
}
